/**
 * Dynamic icon mapping utility
 * Maps icon string names from module manifests to Lucide React components
 */

import * as LucideIcons from 'lucide-react'
import { ComponentType } from 'react'

// Type for Lucide icon components
type LucideIcon = ComponentType<{ className?: string }>

// Map of icon name strings to Lucide icon components
const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard: LucideIcons.LayoutDashboard,
  ShoppingCart: LucideIcons.ShoppingCart,
  Package: LucideIcons.Package,
  Package2: LucideIcons.Package2,
  Truck: LucideIcons.Truck,
  Ship: LucideIcons.Ship,
  RotateCcw: LucideIcons.RotateCcw,
  Euro: LucideIcons.Euro,
  Users: LucideIcons.Users,
  Bot: LucideIcons.Bot,
  HelpCircle: LucideIcons.HelpCircle,
  Building: LucideIcons.Building,
  UserCog: LucideIcons.UserCog,
  FileText: LucideIcons.FileText,
  Plug: LucideIcons.Plug,
  BarChart3: LucideIcons.BarChart3,
  UserCheck: LucideIcons.UserCheck,
  UserPlus: LucideIcons.UserPlus,
  MapPin: LucideIcons.MapPin,
  GraduationCap: LucideIcons.GraduationCap,
  BookOpen: LucideIcons.BookOpen,
  Workflow: LucideIcons.Workflow,
  Kanban: LucideIcons.Kanban,
  Search: LucideIcons.Search,
  Settings: LucideIcons.Settings,
  Accessibility: LucideIcons.Accessibility,
  Calendar: LucideIcons.Calendar,
  Clock: LucideIcons.Clock,
  Target: LucideIcons.Target,
  Command: LucideIcons.Command,
  GitBranch: LucideIcons.GitBranch,
  Grid: LucideIcons.Grid,
  LayoutGrid: LucideIcons.LayoutGrid,
  Network: LucideIcons.Network,
  ListChecks: LucideIcons.ListChecks,
}

/**
 * Get a Lucide icon component from an icon name string
 * @param iconName - Icon name from module manifest (e.g., 'GraduationCap')
 * @param fallback - Fallback icon component (default: LayoutDashboard)
 * @returns Lucide icon component
 */
export function getIconComponent(
  iconName: string | undefined,
  fallback: LucideIcon = LucideIcons.LayoutDashboard
): LucideIcon {
  if (!iconName) {
    return fallback
  }

  // Try direct lookup first
  if (iconMap[iconName]) {
    return iconMap[iconName]
  }

  // Try case-insensitive lookup
  const lowerIconName = iconName.toLowerCase()
  const found = Object.keys(iconMap).find(
    (key) => key.toLowerCase() === lowerIconName
  )

  if (found) {
    return iconMap[found]
  }

  // Fallback to default
  return fallback
}

/**
 * Check if an icon name is available in the icon map
 * @param iconName - Icon name to check
 * @returns True if icon is available
 */
export function hasIcon(iconName: string | undefined): boolean {
  if (!iconName) {
    return false
  }

  return (
    iconMap[iconName] !== undefined ||
    Object.keys(iconMap).some(
      (key) => key.toLowerCase() === iconName.toLowerCase()
    )
  )
}
